/**
 * "Teste o Premium por 30 dias sem pagar nada a mais" (PIT-535) on the billing page: the offer, the
 * PIX authorization still waiting for the bank app, the trial while it runs (with "Voltar ao
 * Básico"), and the Basic charge after giving up. Every screen says the date and amount of the next
 * charge (shared/premium_trial.ts). On PIX the trial is 26–30 days (Woovi charges on days 1–27), so
 * the copy next to the date never says "30 dias": it says nothing is charged until that date.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import type { PremiumTrialPageState } from "../../../shared/premium_trial.js";
import { formatBillingDayAndTime } from "../lib/formatBillingDate.js";

/**
 * "Voltar ao Básico" is open until `give_up_until` (premiumTrialGiveUpClosesAt: PIX at 00:00 BRT the
 * day before the charge day, card at 00:00 BRT on it). The server refuses a late one too (409
 * premium_trial_give_up_closed).
 */
export function premiumTrialGiveUpOpen(state: PremiumTrialPageState | null | undefined, now: number): boolean {
  if (state?.status !== "active" || state.can_give_up === false) return false;
  return !state.give_up_until || now < Date.parse(state.give_up_until);
}

export function PremiumTrialPanel({
  state,
  paymentMethod,
  money,
  formatDate,
  busy,
  error,
  onAccept,
  onResumePix,
  onGiveUp,
  beforeAccept,
}: {
  state: PremiumTrialPageState;
  paymentMethod: string;
  money: (cents: number) => string;
  formatDate: (iso: string) => string;
  busy: boolean;
  error: string | null;
  onAccept: () => void;
  /** pending_authorization: reopen the same PIX authorization (never a new one). */
  onResumePix: () => void;
  onGiveUp: () => void;
  /** Shown above "Testar 30 dias" (the phone field a PIX customer without a usable one fills in). */
  beforeAccept?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const [confirmGiveUp, setConfirmGiveUp] = useState(false);
  const date = formatDate(state.first_charge_at);
  const giveUpUntil = state.status === "active" ? state.give_up_until || null : null;
  // The page can stay open past the deadline: drop the button then, without a reload.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!giveUpUntil) return;
    const wait = Date.parse(giveUpUntil) - Date.now();
    // setTimeout fires at once beyond 2^31 ms (~24 days); a reload covers that stretch.
    if (!(wait > 0) || wait > 2 ** 31 - 1) return;
    const timer = window.setTimeout(() => setNow(Date.now()), wait);
    return () => window.clearTimeout(timer);
  }, [giveUpUntil]);
  /** At a midnight the day before is the last one ("até 24 de novembro"); otherwise the day and time. */
  const giveUpDeadline = (iso: string) => {
    const at = new Date(iso);
    if (at.getHours() === 0 && at.getMinutes() === 0) {
      return formatBillingDayAndTime(new Date(at.getTime() - 1), i18n.language).date;
    }
    return t("integrated.dateAtTime", formatBillingDayAndTime(at, i18n.language));
  };
  const errorLine = error ? (
    <p className="text-sm text-danger mt-2" role="alert">
      {error}
    </p>
  ) : null;

  if (state.status === "given_up") {
    return (
      <div className="mt-5 rounded-lg border border-black/[0.08] bg-black/[0.02] p-4" role="status">
        <div className="font-semibold text-sm">{t("integrated.premiumTrial.givenUpTitle")}</div>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">
          {t("integrated.premiumTrial.givenUpBody", { date, basic: money(state.basic_cents) })}
        </p>
        {errorLine}
      </div>
    );
  }

  if (state.status === "pending_authorization") {
    return (
      <div className="mt-5 rounded-lg border border-warning/40 bg-warning/[0.06] p-4" role="status">
        <div className="font-semibold text-sm">{t("integrated.premiumTrial.pendingTitle")}</div>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">{t("integrated.premiumTrial.pendingBody")}</p>
        <p className="text-xs text-ink-2 mt-2 leading-relaxed">
          {t("integrated.premiumTrial.pendingFirstCharge", { date, premium: money(state.premium_cents) })}
        </p>
        <button type="button" className="btn-primary text-sm mt-3" disabled={busy} onClick={onResumePix}>
          {t("integrated.premiumTrial.pendingButton")}
        </button>
        {errorLine}
      </div>
    );
  }

  if (state.status === "active") {
    const giveUpOpen = premiumTrialGiveUpOpen(state, now);
    return (
      <div className="mt-5 rounded-lg border border-emerald-600/35 bg-emerald-600/[0.08] p-4" role="status">
        <div className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
          {t("integrated.premiumTrial.activeTitle")}
        </div>
        <p className="text-sm text-ink-2 mt-2 leading-relaxed">
          {t("integrated.premiumTrial.activeBody", { date, premium: money(state.premium_cents) })}
        </p>
        {!giveUpOpen ? (
          <p className="text-xs text-ink-2 mt-2 leading-relaxed">{t("integrated.premiumTrial.giveUpClosed")}</p>
        ) : giveUpUntil ? (
          <p className="text-xs text-ink-2 mt-2 leading-relaxed">
            {t("integrated.premiumTrial.activeGiveUpHintUntil", {
              until: giveUpDeadline(giveUpUntil),
              date,
              basic: money(state.basic_cents),
            })}
          </p>
        ) : (
          <p className="text-xs text-ink-2 mt-2 leading-relaxed">
            {t("integrated.premiumTrial.activeGiveUpHint", { date, basic: money(state.basic_cents) })}
          </p>
        )}
        {!giveUpOpen ? null : confirmGiveUp ? (
          <div className="mt-3 space-y-2">
            <p className="text-sm font-medium">
              {t("integrated.premiumTrial.giveUpConfirm", { date, basic: money(state.basic_cents) })}
            </p>
            <div className="flex gap-2 flex-wrap">
              <button type="button" className="btn-primary text-sm" disabled={busy} onClick={onGiveUp}>
                {t("integrated.premiumTrial.giveUpConfirmButton")}
              </button>
              <button type="button" className="btn-ghost text-sm" disabled={busy} onClick={() => setConfirmGiveUp(false)}>
                {t("integrated.premiumTrial.giveUpKeep")}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn-ghost text-xs px-3 py-1.5 mt-3"
            disabled={busy}
            onClick={() => setConfirmGiveUp(true)}
          >
            {t("integrated.premiumTrial.giveUpButton")}
          </button>
        )}
        {errorLine}
      </div>
    );
  }

  return (
    <aside className="checkout-offer-banner mt-5" aria-label={t("integrated.premiumTrial.seal")}>
      <div className="checkout-offer-seal">
        <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {t("integrated.premiumTrial.seal")}
      </div>
      <div className="font-semibold text-base mt-2">{t("integrated.premiumTrial.offerTitle")}</div>
      <p className="checkout-offer-banner-body">{t("integrated.premiumTrial.offerBody")}</p>
      <p className="text-sm text-ink-2 mt-2 leading-relaxed">
        {t("integrated.premiumTrial.offerFirstCharge", { date, premium: money(state.premium_cents) })}
      </p>
      <p className="text-xs text-ink-2 mt-1 leading-relaxed">
        {/* PIX closes "Voltar ao Básico" the day before the charge (premiumTrialGiveUpOpen). */}
        {t(paymentMethod === "openpix" ? "integrated.premiumTrial.offerGiveUpPix" : "integrated.premiumTrial.offerGiveUp", {
          basic: money(state.basic_cents),
        })}
      </p>
      {paymentMethod === "openpix" && (
        <p className="text-xs text-ink-2 mt-1 leading-relaxed">{t("integrated.premiumTrial.offerPixNote")}</p>
      )}
      {beforeAccept}
      <button type="button" className="btn-primary mt-3 w-full sm:w-auto" disabled={busy} onClick={onAccept}>
        {t("integrated.premiumTrial.acceptButton")}
      </button>
      {errorLine}
    </aside>
  );
}
